/**
 * VorXOC Integrations Catalog
 * 303 integrations sourced from Sekoia.io catalog, mapped to Azure Sentinel data connectors.
 * fieldGroups: simplified API-key / host connection fields per integration.
 */

const api = (extra = []) => ({
  title: 'Connection',
  fields: [
    { key: 'host',    label: 'Host / API URL', type: 'text',     placeholder: 'https://api.example.com', required: true  },
    { key: 'api_key', label: 'API Key / Token', type: 'password', placeholder: 'Your API key or token',   required: true  },
    ...extra,
  ],
});

const syslog = () => ({
  title: 'Syslog / CEF Forward',
  fields: [
    { key: 'syslog_host', label: 'Syslog Host', type: 'text', placeholder: 'IP or hostname of your log forwarder', required: true },
    { key: 'syslog_port', label: 'Port',         type: 'text', placeholder: '514',                                  required: false },
  ],
});

const oauth = (extra = []) => ({
  title: 'OAuth2 Credentials',
  fields: [
    { key: 'client_id',     label: 'Client ID',     type: 'text',     placeholder: 'OAuth2 Client ID',     required: true },
    { key: 'client_secret', label: 'Client Secret', type: 'password', placeholder: 'OAuth2 Client Secret', required: true },
    { key: 'tenant_id',     label: 'Tenant / Org ID', type: 'text',   placeholder: 'Tenant or Org ID',     required: false },
    ...extra,
  ],
});

// ─── Catalog ──────────────────────────────────────────────────────────────────

export const CATALOG = [

  // ══════════════════════════════════════════
  // SIEM
  // ══════════════════════════════════════════
  {
    id: 'microsoft-sentinel',
    name: 'Microsoft Sentinel',
    vendor: 'Microsoft',
    category: 'SIEM',
    description: 'Cloud-native SIEM and SOAR. Centralises security data from across your enterprise for intelligent threat detection and automated response.',
    fieldGroups: [
      {
        title: 'Log Analytics Workspace',
        hint: 'Azure Portal → Log Analytics workspaces → your workspace → Agents',
        fields: [
          { key: 'workspace_id', label: 'Workspace ID',  type: 'text',     placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx', required: true,  tip: 'Unique GUID of your Log Analytics workspace.' },
          { key: 'primary_key',  label: 'Primary Key',   type: 'password', placeholder: 'Base64 encoded workspace key',          required: true,  tip: 'Shared key for HTTP Data Collector API authentication.' },
        ],
      },
      {
        title: 'Azure AD App Registration',
        hint: 'Azure Portal → Azure Active Directory → App registrations → your app',
        fields: [
          { key: 'tenant_id',     label: 'Tenant ID',        type: 'text',     placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx', required: true },
          { key: 'client_id',     label: 'Client (App) ID',  type: 'text',     placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx', required: true },
          { key: 'client_secret', label: 'Client Secret',    type: 'password', placeholder: 'App registration secret value',        required: true },
        ],
      },
      {
        title: 'Resource Details',
        hint: 'Azure Portal → Microsoft Sentinel → your workspace → Overview',
        fields: [
          { key: 'subscription_id', label: 'Subscription ID',     type: 'text', placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx', required: true },
          { key: 'resource_group',  label: 'Resource Group Name', type: 'text', placeholder: 'rg-sentinel-prod',                     required: true },
          { key: 'workspace_name',  label: 'Workspace Name',      type: 'text', placeholder: 'law-sentinel-prod',                    required: true },
        ],
      },
    ],
    docsUrl: 'https://docs.microsoft.com/azure/sentinel/',
  },
  {
    id: 'splunk',
    name: 'Splunk SIEM',
    vendor: 'Splunk',
    category: 'SIEM',
    description: 'Industry-leading SIEM platform for real-time monitoring, searching and analysing machine-generated data.',
    fieldGroups: [{ title: 'Connection', fields: [
      { key: 'host',  label: 'Splunk Host',    type: 'text',     placeholder: 'splunk.company.com',    required: true },
      { key: 'port',  label: 'Port',            type: 'text',     placeholder: '8089',                  required: true },
      { key: 'token', label: 'API Token',       type: 'password', placeholder: 'Splunk HEC / REST token', required: true },
      { key: 'index', label: 'Default Index',   type: 'text',     placeholder: 'main',                  required: false },
    ]}],
    docsUrl: 'https://docs.splunk.com',
  },
  {
    id: 'qradar',
    name: 'IBM QRadar',
    vendor: 'IBM',
    category: 'SIEM',
    description: 'QRadar delivers real-time threat detection, prioritised alerts and automated response.',
    fieldGroups: [{ title: 'Connection', fields: [
      { key: 'host',      label: 'QRadar Host', type: 'text',     placeholder: 'qradar.company.com', required: true },
      { key: 'api_token', label: 'SEC Token',   type: 'password', placeholder: 'QRadar SEC token',   required: true },
      { key: 'version',   label: 'API Version', type: 'text',     placeholder: '20.0',               required: false },
    ]}],
    docsUrl: 'https://www.ibm.com/docs/qradar',
  },
  {
    id: 'elastic-siem',
    name: 'Elastic SIEM',
    vendor: 'Elastic',
    category: 'SIEM',
    description: 'Open, scalable SIEM powered by Elasticsearch for threat detection and investigation.',
    fieldGroups: [{ title: 'Connection', fields: [
      { key: 'cloud_id', label: 'Cloud ID / Host', type: 'text',     placeholder: 'https://elastic.cloud:9243', required: true },
      { key: 'api_key',  label: 'API Key',          type: 'password', placeholder: 'Base64 encoded API key',     required: true },
      { key: 'index',    label: 'Index Pattern',    type: 'text',     placeholder: '.alerts-security*',          required: false },
    ]}],
    docsUrl: 'https://www.elastic.co/siem',
  },

  // ══════════════════════════════════════════
  // Endpoint / EDR
  // ══════════════════════════════════════════
  {
    id: 'crowdstrike',
    name: 'CrowdStrike Falcon',
    vendor: 'CrowdStrike',
    category: 'Endpoint',
    description: 'Cloud-native endpoint protection platform with AI-driven threat prevention and real-time response.',
    fieldGroups: [{ ...oauth() }],
    docsUrl: 'https://falcon.crowdstrike.com/documentation',
  },
  {
    id: 'crowdstrike-telemetry',
    name: 'CrowdStrike Falcon Telemetry',
    vendor: 'CrowdStrike',
    category: 'Endpoint',
    description: 'Stream raw event telemetry from CrowdStrike Falcon Data Replicator.',
    fieldGroups: [{ ...oauth([{ key: 'stream_name', label: 'Feed Name', type: 'text', placeholder: 'falcon-data-replicator', required: false }]) }],
    docsUrl: 'https://falcon.crowdstrike.com/documentation/page/c8a198ed/falcon-data-replicator',
  },
  {
    id: 'crowdstrike-mobile',
    name: 'CrowdStrike Falcon for Mobile',
    vendor: 'CrowdStrike',
    category: 'Endpoint',
    description: 'Mobile threat defence and device telemetry via CrowdStrike Falcon for Mobile.',
    fieldGroups: [{ ...oauth() }],
    docsUrl: 'https://falcon.crowdstrike.com/documentation',
  },
  {
    id: 'defender',
    name: 'Microsoft Defender XDR',
    vendor: 'Microsoft',
    category: 'Endpoint',
    description: 'Unified endpoint security platform with preventive protection, post-breach detection and automated investigation.',
    fieldGroups: [oauth().fields && {
      title: 'Azure AD',
      fields: [
        { key: 'tenant_id',     label: 'Tenant ID',     type: 'text',     placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx', required: true },
        { key: 'client_id',     label: 'Client ID',     type: 'text',     placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx', required: true },
        { key: 'client_secret', label: 'Client Secret', type: 'password', placeholder: 'App registration secret',             required: true },
      ],
    }],
    docsUrl: 'https://docs.microsoft.com/microsoft-365/security/defender/',
  },
  {
    id: 'sentinelone',
    name: 'SentinelOne EDR',
    vendor: 'SentinelOne',
    category: 'Endpoint',
    description: 'Autonomous AI-powered endpoint protection that prevents, detects, and responds to threats in real time.',
    fieldGroups: [{ title: 'Connection', fields: [
      { key: 'host',    label: 'Console URL', type: 'text',     placeholder: 'https://yourtenant.sentinelone.net', required: true },
      { key: 'api_key', label: 'API Token',   type: 'password', placeholder: 'S1 API Token',                       required: true },
    ]}],
    docsUrl: 'https://usea1.sentinelone.net/docs/',
  },
  {
    id: 'sentinelone-idr',
    name: 'SentinelOne Identity Detection & Response',
    vendor: 'SentinelOne',
    category: 'Endpoint',
    description: 'Identity threat detection and response integrated with SentinelOne platform.',
    fieldGroups: [api()],
    docsUrl: 'https://usea1.sentinelone.net/docs/',
  },
  {
    id: 'carbon-black',
    name: 'VMware Carbon Black',
    vendor: 'VMware',
    category: 'Endpoint',
    description: "Endpoint and workload protection leveraging VMware's Carbon Black Cloud behavioural EDR.",
    fieldGroups: [{ title: 'Connection', fields: [
      { key: 'org_key',  label: 'Org Key',    type: 'text',     placeholder: 'Carbon Black Org Key', required: true },
      { key: 'api_id',   label: 'API ID',     type: 'text',     placeholder: 'API ID',               required: true },
      { key: 'api_key',  label: 'API Secret', type: 'password', placeholder: 'API Secret Key',       required: true },
    ]}],
    docsUrl: 'https://developer.carbonblack.com',
  },
  {
    id: 'palo-alto-xdr',
    name: 'Palo Alto Cortex XDR',
    vendor: 'Palo Alto Networks',
    category: 'Endpoint',
    description: 'Extended detection and response platform from Palo Alto Networks, correlating endpoint, network and cloud data.',
    fieldGroups: [{ title: 'API Credentials', fields: [
      { key: 'fqdn',       label: 'Tenant FQDN', type: 'text',     placeholder: 'api-xyz.xdr.us.paloaltonetworks.com', required: true },
      { key: 'api_key_id', label: 'API Key ID',  type: 'text',     placeholder: 'API Key ID',                          required: true },
      { key: 'api_key',    label: 'API Key',     type: 'password', placeholder: 'API Key',                             required: true },
    ]}],
    docsUrl: 'https://docs.paloaltonetworks.com/cortex/cortex-xdr',
  },
  {
    id: 'palo-alto-cortex-datalake',
    name: 'Palo Alto Cortex Data Lake',
    vendor: 'Palo Alto Networks',
    category: 'Endpoint',
    description: 'Centralised cloud-based logging service for Palo Alto Networks products.',
    fieldGroups: [api()],
    docsUrl: 'https://docs.paloaltonetworks.com/cortex/cortex-data-lake',
  },
  {
    id: 'sophos-edr',
    name: 'Sophos EDR',
    vendor: 'Sophos',
    category: 'Endpoint',
    description: 'Endpoint detection and response with Intercept X and live threat response capabilities.',
    fieldGroups: [oauth()],
    docsUrl: 'https://docs.sophos.com/central/customer/en-us/docs',
  },
  {
    id: 'sophos-firewall',
    name: 'Sophos Firewall',
    vendor: 'Sophos',
    category: 'Network',
    description: 'Next-generation firewall with deep packet inspection and integrated endpoint communication.',
    fieldGroups: [api()],
    docsUrl: 'https://docs.sophos.com/nsg/sophos-firewall/',
  },
  {
    id: 'trellix-edr',
    name: 'Trellix EDR',
    vendor: 'Trellix',
    category: 'Endpoint',
    description: 'Endpoint detection and response platform (formerly McAfee MVISION EDR).',
    fieldGroups: [api()],
    docsUrl: 'https://docs.trellix.com',
  },
  {
    id: 'trellix-epo',
    name: 'Trellix ePO',
    vendor: 'Trellix',
    category: 'Endpoint',
    description: 'Centralised security management platform for Trellix endpoint products (SaaS).',
    fieldGroups: [api()],
    docsUrl: 'https://docs.trellix.com',
  },
  {
    id: 'trellix-epo-onprem',
    name: 'Trellix ePO On-Premises',
    vendor: 'Trellix',
    category: 'Endpoint',
    description: 'On-premises deployment of Trellix ePolicy Orchestrator.',
    fieldGroups: [api()],
    docsUrl: 'https://docs.trellix.com',
  },
  {
    id: 'trellix-atd',
    name: 'Trellix Advanced Threat Defense',
    vendor: 'Trellix',
    category: 'Endpoint',
    description: 'Advanced malware analysis and sandbox detonation from Trellix (formerly FireEye).',
    fieldGroups: [api()],
    docsUrl: 'https://docs.trellix.com',
  },
  {
    id: 'trellix-network-security',
    name: 'Trellix Network Security',
    vendor: 'Trellix',
    category: 'Network',
    description: 'Network traffic analysis and intrusion prevention from Trellix.',
    fieldGroups: [syslog()],
    docsUrl: 'https://docs.trellix.com',
  },
  {
    id: 'withsecure-elements',
    name: 'WithSecure Elements',
    vendor: 'WithSecure',
    category: 'Endpoint',
    description: 'Cloud-delivered endpoint protection, EDR and vulnerability management from WithSecure (F-Secure).',
    fieldGroups: [oauth()],
    docsUrl: 'https://www.withsecure.com/en/solutions/software-and-services/elements',
  },
  {
    id: 'harfanglab-edr',
    name: 'HarfangLab EDR',
    vendor: 'HarfangLab',
    category: 'Endpoint',
    description: 'European EDR solution with on-premises and SaaS deployment options.',
    fieldGroups: [api()],
    docsUrl: 'https://docs.harfanglab.io',
  },
  {
    id: 'cybereason-edr',
    name: 'Cybereason EDR',
    vendor: 'Cybereason',
    category: 'Endpoint',
    description: 'AI-driven endpoint detection and response with MalOp™ detection engine.',
    fieldGroups: [{ title: 'Connection', fields: [
      { key: 'host',     label: 'Server URL',  type: 'text',     placeholder: 'https://yourserver.cybereason.net', required: true },
      { key: 'username', label: 'Username',    type: 'text',     placeholder: 'API username', required: true },
      { key: 'password', label: 'Password',    type: 'password', placeholder: 'API password', required: true },
    ]}],
    docsUrl: 'https://nest.cybereason.com',
  },
  {
    id: 'tehtris-edr',
    name: 'TEHTRIS EDR',
    vendor: 'TEHTRIS',
    category: 'Endpoint',
    description: 'Hyper-automated EDR solution designed for enterprise-level threat neutralisation.',
    fieldGroups: [api()],
    docsUrl: 'https://tehtris.com/en/products/edr',
  },
  {
    id: 'nucleon-edr',
    name: 'Nucleon EDR',
    vendor: 'Nucleon',
    category: 'Endpoint',
    description: 'Endpoint detection and response with real-time behavioural analysis.',
    fieldGroups: [api()],
    docsUrl: 'https://nucleon.sh',
  },
  {
    id: 'bitdefender-gravityzone',
    name: 'Bitdefender GravityZone',
    vendor: 'Bitdefender',
    category: 'Endpoint',
    description: 'Enterprise endpoint security and EDR with Bitdefender GravityZone platform.',
    fieldGroups: [api([{ key: 'company_id', label: 'Company ID', type: 'text', placeholder: 'GravityZone company ID', required: true }])],
    docsUrl: 'https://www.bitdefender.com/business/products/gravityzone',
  },
  {
    id: 'eset-protect',
    name: 'ESET Protect / Inspect',
    vendor: 'ESET',
    category: 'Endpoint',
    description: 'ESET PROTECT platform for endpoint management with optional ESET Inspect EDR module.',
    fieldGroups: [api()],
    docsUrl: 'https://help.eset.com/protect_admin/',
  },
  {
    id: 'kaspersky-endpoint',
    name: 'Kaspersky Endpoint Security',
    vendor: 'Kaspersky',
    category: 'Endpoint',
    description: 'Multi-layered endpoint protection from Kaspersky with EDR capabilities.',
    fieldGroups: [api()],
    docsUrl: 'https://support.kaspersky.com/KESWin',
  },
  {
    id: 'trend-micro-apex',
    name: 'Trend Micro Apex One / Vision One',
    vendor: 'Trend Micro',
    category: 'Endpoint',
    description: 'Automated threat detection and response across endpoints with Trend Micro Vision One.',
    fieldGroups: [api([{ key: 'region', label: 'Region', type: 'text', placeholder: 'us-east-1', required: false }])],
    docsUrl: 'https://docs.trendmicro.com/en-us/enterprise/trend-micro-vision-one',
  },
  {
    id: 'trend-micro-vision-workbench',
    name: 'Trend Micro Vision One Workbench',
    vendor: 'Trend Micro',
    category: 'Endpoint',
    description: 'XDR workbench for correlating alerts across endpoints, email, network and cloud.',
    fieldGroups: [api()],
    docsUrl: 'https://docs.trendmicro.com/en-us/enterprise/trend-micro-vision-one',
  },
  {
    id: 'trend-micro-vision-oat',
    name: 'Trend Micro Vision One – OAT',
    vendor: 'Trend Micro',
    category: 'Endpoint',
    description: 'Observed Attack Techniques telemetry from Trend Micro Vision One.',
    fieldGroups: [api()],
    docsUrl: 'https://docs.trendmicro.com/en-us/enterprise/trend-micro-vision-one',
  },
  {
    id: 'lookout-mobile',
    name: 'Lookout Mobile Endpoint Security',
    vendor: 'Lookout',
    category: 'Endpoint',
    description: 'Mobile threat defence detecting device, network and app attacks on iOS and Android.',
    fieldGroups: [api()],
    docsUrl: 'https://docs.lookout.com',
  },
  {
    id: 'symantec-endpoint',
    name: 'Symantec / Broadcom Endpoint',
    vendor: 'Broadcom',
    category: 'Endpoint',
    description: 'Endpoint protection and EDR from Symantec (Broadcom), formerly Symantec Endpoint Protection.',
    fieldGroups: [syslog()],
    docsUrl: 'https://techdocs.broadcom.com/us/en/symantec-security-software/endpoint-security-and-management',
  },
  {
    id: 'tanium',
    name: 'Tanium',
    vendor: 'Tanium',
    category: 'Endpoint',
    description: 'Real-time endpoint management and security at enterprise scale.',
    fieldGroups: [api([{ key: 'session_token', label: 'Session Token', type: 'password', placeholder: 'Tanium session token', required: false }])],
    docsUrl: 'https://docs.tanium.com',
  },
  {
    id: 'watchguard-epdr',
    name: 'WatchGuard EPDR',
    vendor: 'WatchGuard',
    category: 'Endpoint',
    description: 'Endpoint protection, detection and response combining AV and EDR capabilities.',
    fieldGroups: [oauth()],
    docsUrl: 'https://www.watchguard.com/wgrd-products/security-platform/endpoint-security',
  },
  {
    id: 'stormshield-ses',
    name: 'Stormshield SES',
    vendor: 'Stormshield',
    category: 'Endpoint',
    description: 'French endpoint security solution with behavioural blocking and EDR.',
    fieldGroups: [api()],
    docsUrl: 'https://documentation.stormshield.eu',
  },
  {
    id: 'sekoia-agent',
    name: 'SEKOIA.IO Agent',
    vendor: 'SEKOIA',
    category: 'Endpoint',
    description: 'Lightweight agent for Windows, Linux and macOS endpoint telemetry collection.',
    fieldGroups: [api()],
    docsUrl: 'https://docs.sekoia.io/xdr/features/collect/integrations/endpoint/sekoiaio_agent/',
  },
  {
    id: 'azure-windows',
    name: 'Azure Windows (WEF)',
    vendor: 'Microsoft',
    category: 'Endpoint',
    description: 'Windows Event Forwarding from Azure-hosted VMs to Microsoft Sentinel.',
    fieldGroups: [syslog()],
    docsUrl: 'https://docs.microsoft.com/azure/sentinel/connect-windows-security-events',
  },
  {
    id: 'windows',
    name: 'Windows (On-Premises)',
    vendor: 'Microsoft',
    category: 'Endpoint',
    description: 'Collect Windows Security Event logs from on-premises hosts via Azure Arc or MMA agent.',
    fieldGroups: [syslog()],
    docsUrl: 'https://docs.microsoft.com/azure/sentinel/connect-windows-security-events',
  },
  {
    id: 'windows-log-insight',
    name: 'Windows Log Insight',
    vendor: 'Microsoft',
    category: 'Endpoint',
    description: 'Forward Windows event logs using Log Insight agent.',
    fieldGroups: [syslog()],
    docsUrl: 'https://docs.microsoft.com/azure/sentinel/',
  },

  // ══════════════════════════════════════════
  // Network / Firewall
  // ══════════════════════════════════════════
  {
    id: 'palo-alto-ngfw',
    name: 'Palo Alto NGFW',
    vendor: 'Palo Alto Networks',
    category: 'Network',
    description: 'Next-generation firewall with App-ID, User-ID and WildFire integration.',
    fieldGroups: [syslog()],
    docsUrl: 'https://docs.paloaltonetworks.com',
  },
  {
    id: 'fortinet-fortigate',
    name: 'Fortinet FortiGate',
    vendor: 'Fortinet',
    category: 'Network',
    description: 'Industry-leading NGFW and SD-WAN platform with FortiOS.',
    fieldGroups: [syslog()],
    docsUrl: 'https://docs.fortinet.com',
  },
  {
    id: 'fortinet-fortiproxy',
    name: 'Fortinet FortiProxy',
    vendor: 'Fortinet',
    category: 'Network',
    description: 'Dedicated web proxy gateway with advanced content filtering.',
    fieldGroups: [syslog()],
    docsUrl: 'https://docs.fortinet.com',
  },
  {
    id: 'fortinet-fortiweb',
    name: 'Fortinet FortiWeb',
    vendor: 'Fortinet',
    category: 'Network',
    description: 'Web application and API protection firewall from Fortinet.',
    fieldGroups: [syslog()],
    docsUrl: 'https://docs.fortinet.com',
  },
  {
    id: 'fortinet-fortimail',
    name: 'Fortinet FortiMail',
    vendor: 'Fortinet',
    category: 'Email',
    description: 'Secure email gateway with advanced threat protection from Fortinet.',
    fieldGroups: [syslog()],
    docsUrl: 'https://docs.fortinet.com',
  },
  {
    id: 'check-point-ngfw',
    name: 'Check Point NGFW',
    vendor: 'Check Point',
    category: 'Network',
    description: 'Infinity architecture NGFW with SmartEvent, Threat Extraction and SandBlast.',
    fieldGroups: [syslog()],
    docsUrl: 'https://sc1.checkpoint.com/documents/latest',
  },
  {
    id: 'cisco-secure-firewall',
    name: 'Cisco Secure Firewall',
    vendor: 'Cisco',
    category: 'Network',
    description: 'Cisco ASA/FTD next-generation firewall with Firepower threat intelligence.',
    fieldGroups: [syslog()],
    docsUrl: 'https://www.cisco.com/c/en/us/products/security/firewalls',
  },
  {
    id: 'cisco-ios',
    name: 'Cisco IOS Router & Switch',
    vendor: 'Cisco',
    category: 'Network',
    description: 'Syslog forwarding from Cisco IOS routers and switches.',
    fieldGroups: [syslog()],
    docsUrl: 'https://www.cisco.com',
  },
  {
    id: 'cisco-nxos',
    name: 'Cisco NX-OS',
    vendor: 'Cisco',
    category: 'Network',
    description: 'Network event logs from Cisco NX-OS data centre switches.',
    fieldGroups: [syslog()],
    docsUrl: 'https://www.cisco.com',
  },
  {
    id: 'cisco-meraki',
    name: 'Cisco Meraki MX',
    vendor: 'Cisco',
    category: 'Network',
    description: 'Cloud-managed security appliance and SD-WAN from Cisco Meraki.',
    fieldGroups: [api([{ key: 'org_id', label: 'Organisation ID', type: 'text', placeholder: 'Meraki Org ID', required: true }])],
    docsUrl: 'https://documentation.meraki.com',
  },
  {
    id: 'cisco-esa',
    name: 'Cisco ESA (Email Security)',
    vendor: 'Cisco',
    category: 'Email',
    description: 'Cisco Email Security Appliance log forwarding via syslog.',
    fieldGroups: [syslog()],
    docsUrl: 'https://www.cisco.com',
  },
  {
    id: 'cisco-umbrella-dns',
    name: 'Cisco Umbrella DNS',
    vendor: 'Cisco',
    category: 'Network',
    description: 'DNS-layer security logs from Cisco Umbrella.',
    fieldGroups: [api([{ key: 'org_id', label: 'Organisation ID', type: 'text', placeholder: 'Umbrella Org ID', required: true }])],
    docsUrl: 'https://docs.umbrella.com',
  },
  {
    id: 'cisco-umbrella-proxy',
    name: 'Cisco Umbrella Proxy',
    vendor: 'Cisco',
    category: 'Network',
    description: 'Proxy and HTTP logs from Cisco Umbrella Secure Web Gateway.',
    fieldGroups: [api()],
    docsUrl: 'https://docs.umbrella.com',
  },
  {
    id: 'cisco-umbrella-ip',
    name: 'Cisco Umbrella IP',
    vendor: 'Cisco',
    category: 'Network',
    description: 'IP-layer enforcement logs from Cisco Umbrella.',
    fieldGroups: [api()],
    docsUrl: 'https://docs.umbrella.com',
  },
  {
    id: 'cisco-swa',
    name: 'Cisco Secure Web Appliance',
    vendor: 'Cisco',
    category: 'Network',
    description: 'Proxy and URL filtering logs from Cisco Secure Web Appliance (formerly WSA).',
    fieldGroups: [syslog()],
    docsUrl: 'https://www.cisco.com',
  },
  {
    id: 'cisco-sdwan',
    name: 'Cisco Catalyst SD-WAN',
    vendor: 'Cisco',
    category: 'Network',
    description: 'Network flow and security events from Cisco Catalyst SD-WAN (Viptela).',
    fieldGroups: [api()],
    docsUrl: 'https://www.cisco.com/c/en/us/solutions/enterprise-networks/sd-wan',
  },
  {
    id: 'juniper-ngfw',
    name: 'Juniper Networks NGFW',
    vendor: 'Juniper',
    category: 'Network',
    description: 'SRX series next-generation firewall event logs from Juniper Networks.',
    fieldGroups: [syslog()],
    docsUrl: 'https://www.juniper.net/documentation',
  },
  {
    id: 'juniper-switches',
    name: 'Juniper Switches',
    vendor: 'Juniper',
    category: 'Network',
    description: 'EX series switch logs forwarded via syslog.',
    fieldGroups: [syslog()],
    docsUrl: 'https://www.juniper.net/documentation',
  },
  {
    id: 'sonicwall-ngfw',
    name: 'SonicWall NG Firewall',
    vendor: 'SonicWall',
    category: 'Network',
    description: 'Next-generation firewall event logs from SonicWall NSA and TZ series.',
    fieldGroups: [syslog()],
    docsUrl: 'https://www.sonicwall.com/techdocs',
  },
  {
    id: 'sonicwall-sma',
    name: 'SonicWall Secure Mobile Access',
    vendor: 'SonicWall',
    category: 'Network',
    description: 'VPN and remote access logs from SonicWall SMA.',
    fieldGroups: [syslog()],
    docsUrl: 'https://www.sonicwall.com/techdocs',
  },
  {
    id: 'f5-big-ip',
    name: 'F5 BIG-IP',
    vendor: 'F5',
    category: 'Network',
    description: 'Application delivery and security logs from F5 BIG-IP TMOS platform.',
    fieldGroups: [syslog()],
    docsUrl: 'https://techdocs.f5.com',
  },
  {
    id: 'f5-distributed-cloud',
    name: 'F5 Distributed Cloud',
    vendor: 'F5',
    category: 'Network',
    description: 'Security and WAF events from F5 Distributed Cloud Services.',
    fieldGroups: [api()],
    docsUrl: 'https://docs.cloud.f5.com',
  },
  {
    id: 'f5-nginx',
    name: 'F5 NGINX',
    vendor: 'F5',
    category: 'Network',
    description: 'Access and error logs from NGINX and NGINX App Protect WAF.',
    fieldGroups: [syslog()],
    docsUrl: 'https://docs.nginx.com',
  },
  {
    id: 'zscaler-zia',
    name: 'Zscaler ZIA',
    vendor: 'Zscaler',
    category: 'Network',
    description: 'Cloud-native web proxy and DNS security logs from Zscaler Internet Access.',
    fieldGroups: [api([{ key: 'cloud_name', label: 'Cloud Name', type: 'text', placeholder: 'zscalerthree.net', required: true }])],
    docsUrl: 'https://help.zscaler.com/zia',
  },
  {
    id: 'zscaler-zpa',
    name: 'Zscaler ZPA',
    vendor: 'Zscaler',
    category: 'Network',
    description: 'Zero-trust private access logs from Zscaler Private Access.',
    fieldGroups: [api()],
    docsUrl: 'https://help.zscaler.com/zpa',
  },
  {
    id: 'cloudflare',
    name: 'Cloudflare',
    vendor: 'Cloudflare',
    category: 'Network',
    description: 'DNS, HTTP, firewall and access logs via Cloudflare Logpush.',
    fieldGroups: [api([{ key: 'zone_id', label: 'Zone ID', type: 'text', placeholder: 'Cloudflare Zone ID', required: false }])],
    docsUrl: 'https://developers.cloudflare.com/logs',
  },
  {
    id: 'cloudflare-gateway',
    name: 'Cloudflare Gateway (DNS/HTTP/Network)',
    vendor: 'Cloudflare',
    category: 'Network',
    description: 'DNS, HTTP and network logs from Cloudflare Zero Trust Gateway.',
    fieldGroups: [api()],
    docsUrl: 'https://developers.cloudflare.com/cloudflare-one/analytics/logs',
  },
  {
    id: 'cloudflare-access',
    name: 'Cloudflare Access Requests',
    vendor: 'Cloudflare',
    category: 'Network',
    description: 'Access request audit logs from Cloudflare Zero Trust Access.',
    fieldGroups: [api()],
    docsUrl: 'https://developers.cloudflare.com/cloudflare-one/analytics/logs',
  },
  {
    id: 'akamai-waf',
    name: 'Akamai Web Application Firewall',
    vendor: 'Akamai',
    category: 'Network',
    description: 'Web application firewall security events from Akamai Kona Site Defender.',
    fieldGroups: [api()],
    docsUrl: 'https://techdocs.akamai.com',
  },
  {
    id: 'akamai-guardicore',
    name: 'Akamai Guardicore Segmentation',
    vendor: 'Akamai',
    category: 'Network',
    description: 'Micro-segmentation and lateral movement detection from Guardicore (Akamai).',
    fieldGroups: [api()],
    docsUrl: 'https://techdocs.akamai.com/guardicore',
  },
  {
    id: 'imperva-waf',
    name: 'Imperva WAF',
    vendor: 'Imperva',
    category: 'Network',
    description: 'Web application and API security logs from Imperva Cloud WAF.',
    fieldGroups: [api()],
    docsUrl: 'https://docs.imperva.com',
  },
  {
    id: 'barracuda-cloudgen',
    name: 'Barracuda CloudGen Firewall',
    vendor: 'Barracuda',
    category: 'Network',
    description: 'Next-generation firewall and SD-WAN event logs from Barracuda.',
    fieldGroups: [syslog()],
    docsUrl: 'https://campus.barracuda.com/product/cloudgenfirewall',
  },
  {
    id: 'cato-networks',
    name: 'Cato Networks SASE',
    vendor: 'Cato Networks',
    category: 'Network',
    description: 'Security and network logs from Cato SASE cloud platform.',
    fieldGroups: [api()],
    docsUrl: 'https://support.catonetworks.com',
  },
  {
    id: 'netskope',
    name: 'Netskope',
    vendor: 'Netskope',
    category: 'Network',
    description: 'Cloud access security broker and SSE events from Netskope.',
    fieldGroups: [api()],
    docsUrl: 'https://docs.netskope.com',
  },
  {
    id: 'forcepoint-ngfw',
    name: 'Forcepoint NGFW',
    vendor: 'Forcepoint',
    category: 'Network',
    description: 'Next-generation firewall and intrusion prevention from Forcepoint.',
    fieldGroups: [syslog()],
    docsUrl: 'https://support.forcepoint.com',
  },
  {
    id: 'forcepoint-swg',
    name: 'Forcepoint Secure Web Gateway',
    vendor: 'Forcepoint',
    category: 'Network',
    description: 'Web proxy and content filtering logs from Forcepoint SWG.',
    fieldGroups: [syslog()],
    docsUrl: 'https://support.forcepoint.com',
  },
  {
    id: 'broadcom-swg',
    name: 'Broadcom Secure Web Gateway',
    vendor: 'Broadcom',
    category: 'Network',
    description: 'Web proxy and URL filtering from Broadcom ProxySG / Symantec Web Security.',
    fieldGroups: [syslog()],
    docsUrl: 'https://techdocs.broadcom.com',
  },
  {
    id: 'citrix-netscaler',
    name: 'Citrix NetScaler / ADC',
    vendor: 'Citrix',
    category: 'Network',
    description: 'Application delivery controller and WAF logs from Citrix NetScaler.',
    fieldGroups: [syslog()],
    docsUrl: 'https://docs.citrix.com',
  },
  {
    id: 'haproxy',
    name: 'HAProxy',
    vendor: 'HAProxy',
    category: 'Network',
    description: 'Open-source load balancer and proxy access logs.',
    fieldGroups: [syslog()],
    docsUrl: 'http://www.haproxy.org/#docs',
  },
  {
    id: 'apache-httpd',
    name: 'Apache HTTP Server',
    vendor: 'Apache',
    category: 'Network',
    description: 'Access and error logs from Apache HTTPD web server.',
    fieldGroups: [syslog()],
    docsUrl: 'https://httpd.apache.org/docs/',
  },
  {
    id: 'squid',
    name: 'Squid Proxy',
    vendor: 'Squid',
    category: 'Network',
    description: 'Caching web proxy access logs from Squid.',
    fieldGroups: [syslog()],
    docsUrl: 'http://www.squid-cache.org/Doc/',
  },
  {
    id: 'suricata',
    name: 'Suricata / Stamus Networks',
    vendor: 'OISF',
    category: 'Network',
    description: 'Open-source IDS/IPS/NSM alert and flow events from Suricata.',
    fieldGroups: [syslog()],
    docsUrl: 'https://suricata.io/documentation/',
  },
  {
    id: 'stormshield-sns',
    name: 'Stormshield SNS',
    vendor: 'Stormshield',
    category: 'Network',
    description: 'Next-generation network security gateway from Stormshield.',
    fieldGroups: [syslog()],
    docsUrl: 'https://documentation.stormshield.eu',
  },
  {
    id: 'vectra-cognito',
    name: 'Vectra Cognito Detect',
    vendor: 'Vectra AI',
    category: 'Network',
    description: 'AI-driven network detection and response from Vectra.',
    fieldGroups: [api()],
    docsUrl: 'https://support.vectra.ai',
  },
  {
    id: 'vectra-respond',
    name: 'Vectra Respond UX',
    vendor: 'Vectra AI',
    category: 'Network',
    description: 'Automated threat response and prioritisation from Vectra.',
    fieldGroups: [api()],
    docsUrl: 'https://support.vectra.ai',
  },
  {
    id: 'darktrace',
    name: 'Darktrace Threat Visualizer',
    vendor: 'Darktrace',
    category: 'Network',
    description: 'AI-powered threat detection using unsupervised machine learning.',
    fieldGroups: [api()],
    docsUrl: 'https://customerportal.darktrace.com',
  },
  {
    id: 'extrahop',
    name: 'ExtraHop Reveal(x) 360',
    vendor: 'ExtraHop',
    category: 'Network',
    description: 'Cloud-native network detection and response with real-time packet inspection.',
    fieldGroups: [api()],
    docsUrl: 'https://docs.extrahop.com',
  },
  {
    id: 'radware-defensepro',
    name: 'Radware DefensePro',
    vendor: 'Radware',
    category: 'Network',
    description: 'DDoS protection and intrusion prevention from Radware DefensePro.',
    fieldGroups: [syslog()],
    docsUrl: 'https://portals.radware.com',
  },
  {
    id: 'infoblox-ddi',
    name: 'Infoblox DDI',
    vendor: 'Infoblox',
    category: 'Network',
    description: 'DNS, DHCP and IPAM security events from Infoblox.',
    fieldGroups: [api()],
    docsUrl: 'https://docs.infoblox.com',
  },
  {
    id: 'bind',
    name: 'Bind DNS',
    vendor: 'ISC',
    category: 'Network',
    description: 'DNS query and response logs from ISC BIND.',
    fieldGroups: [syslog()],
    docsUrl: 'https://www.isc.org/bind/',
  },
  {
    id: 'isc-dhcp',
    name: 'ISC DHCP',
    vendor: 'ISC',
    category: 'Network',
    description: 'DHCP lease logs from ISC DHCP server.',
    fieldGroups: [syslog()],
    docsUrl: 'https://www.isc.org/dhcp/',
  },
  {
    id: 'opnsense-pfsense',
    name: 'OPNsense / pfSense',
    vendor: 'Various',
    category: 'Network',
    description: 'Firewall and packet filter logs from OPNsense, pfSense or OpenBSD PF.',
    fieldGroups: [syslog()],
    docsUrl: 'https://docs.opnsense.org',
  },
  {
    id: 'clavister-ngfw',
    name: 'Clavister NGFW',
    vendor: 'Clavister',
    category: 'Network',
    description: 'Next-generation firewall security events from Clavister.',
    fieldGroups: [syslog()],
    docsUrl: 'https://www.clavister.com/documentation',
  },
  {
    id: 'watchguard-firebox',
    name: 'WatchGuard Firebox',
    vendor: 'WatchGuard',
    category: 'Network',
    description: 'Firewall and network security logs from WatchGuard Firebox.',
    fieldGroups: [syslog()],
    docsUrl: 'https://www.watchguard.com/wgrd-support/overview',
  },
  {
    id: 'gatewatcher-aioniq',
    name: 'Gatewatcher AionIQ',
    vendor: 'Gatewatcher',
    category: 'Network',
    description: 'Network detection and response with AI from Gatewatcher AionIQ / Trackwatch.',
    fieldGroups: [api()],
    docsUrl: 'https://www.gatewatcher.com',
  },
  {
    id: 'ogo-waf',
    name: 'OGO WAF',
    vendor: 'OGO',
    category: 'Network',
    description: 'Web application firewall events from OGO.',
    fieldGroups: [syslog()],
    docsUrl: 'https://www.ogo.fr',
  },
  {
    id: 'ubika-waap',
    name: 'Ubika WAAP Gateway',
    vendor: 'Ubika',
    category: 'Network',
    description: 'Web application and API protection gateway from Ubika.',
    fieldGroups: [api()],
    docsUrl: 'https://www.ubikasec.com',
  },
  {
    id: 'datadome',
    name: 'DataDome Bot Protection',
    vendor: 'DataDome',
    category: 'Network',
    description: 'Real-time bot and fraud protection for web applications.',
    fieldGroups: [api()],
    docsUrl: 'https://docs.datadome.co',
  },
  {
    id: 'fastly-ngwaf',
    name: 'Fastly Next-Gen WAF',
    vendor: 'Fastly',
    category: 'Network',
    description: 'WAF alerts and audit logs from Fastly NGWAF (Signal Sciences).',
    fieldGroups: [api()],
    docsUrl: 'https://docs.fastly.com/signalsciences',
  },
  {
    id: 'microsoft-iis',
    name: 'Microsoft IIS',
    vendor: 'Microsoft',
    category: 'Network',
    description: 'Web server access logs from Microsoft Internet Information Services.',
    fieldGroups: [syslog()],
    docsUrl: 'https://docs.microsoft.com/iis',
  },
  {
    id: 'aruba-os',
    name: 'ArubaOS Switch',
    vendor: 'Aruba / HPE',
    category: 'Network',
    description: 'Network event logs from Aruba / HPE access layer switches.',
    fieldGroups: [syslog()],
    docsUrl: 'https://www.arubanetworks.com/techdocs',
  },
  {
    id: 'microsoft-always-on-vpn',
    name: 'Microsoft Always On VPN',
    vendor: 'Microsoft',
    category: 'Network',
    description: 'VPN connection and authentication logs from Windows Always On VPN.',
    fieldGroups: [syslog()],
    docsUrl: 'https://docs.microsoft.com/windows-server/remote/remote-access/vpn',
  },
  {
    id: 'ekinops-oneos',
    name: 'Ekinops OneOS',
    vendor: 'Ekinops',
    category: 'Network',
    description: 'Network OS event logs from Ekinops OneOS-based devices.',
    fieldGroups: [syslog()],
    docsUrl: 'https://www.ekinops.com',
  },
  {
    id: 'netfilter',
    name: 'Netfilter',
    vendor: 'Linux',
    category: 'Network',
    description: 'iptables / nftables packet filter logs from Linux Netfilter.',
    fieldGroups: [syslog()],
    docsUrl: 'https://www.netfilter.org',
  },
  {
    id: 'freeradius',
    name: 'FreeRADIUS',
    vendor: 'FreeRADIUS',
    category: 'Network',
    description: 'RADIUS authentication and accounting logs from FreeRADIUS.',
    fieldGroups: [syslog()],
    docsUrl: 'https://freeradius.org/documentation/',
  },

  // ══════════════════════════════════════════
  // Cloud
  // ══════════════════════════════════════════
  {
    id: 'aws-cloudtrail',
    name: 'AWS CloudTrail',
    vendor: 'Amazon Web Services',
    category: 'Cloud',
    description: 'API call history for your AWS account delivered via CloudTrail to S3 or CloudWatch.',
    fieldGroups: [{ title: 'AWS Credentials', fields: [
      { key: 'access_key_id',     label: 'Access Key ID',     type: 'text',     placeholder: 'AKIA...',            required: true },
      { key: 'secret_access_key', label: 'Secret Access Key', type: 'password', placeholder: 'AWS Secret Key',     required: true },
      { key: 'region',            label: 'Region',             type: 'text',     placeholder: 'us-east-1',          required: true },
      { key: 's3_bucket',         label: 'S3 Bucket',          type: 'text',     placeholder: 'my-cloudtrail-logs', required: true },
    ]}],
    docsUrl: 'https://docs.aws.amazon.com/cloudtrail',
  },
  {
    id: 'aws-guardduty',
    name: 'AWS GuardDuty',
    vendor: 'Amazon Web Services',
    category: 'Cloud',
    description: 'Intelligent threat detection for AWS accounts, workloads and S3 data.',
    fieldGroups: [{ title: 'AWS Credentials', fields: [
      { key: 'access_key_id',     label: 'Access Key ID',     type: 'text',     placeholder: 'AKIA...',       required: true },
      { key: 'secret_access_key', label: 'Secret Access Key', type: 'password', placeholder: 'AWS Secret Key', required: true },
      { key: 'region',            label: 'Region',             type: 'text',     placeholder: 'us-east-1',     required: true },
    ]}],
    docsUrl: 'https://docs.aws.amazon.com/guardduty',
  },
  {
    id: 'aws-s3',
    name: 'AWS S3 Logs',
    vendor: 'Amazon Web Services',
    category: 'Cloud',
    description: 'Server access logs from Amazon S3 buckets.',
    fieldGroups: [api([{ key: 'region', label: 'Region', type: 'text', placeholder: 'us-east-1', required: true }])],
    docsUrl: 'https://docs.aws.amazon.com/s3',
  },
  {
    id: 'aws-vpc-flow',
    name: 'AWS VPC Flow Logs',
    vendor: 'Amazon Web Services',
    category: 'Cloud',
    description: 'Network flow logs from AWS Virtual Private Cloud.',
    fieldGroups: [api([{ key: 'region', label: 'Region', type: 'text', placeholder: 'us-east-1', required: true }])],
    docsUrl: 'https://docs.aws.amazon.com/vpc/latest/userguide/flow-logs.html',
  },
  {
    id: 'aws-cloudfront',
    name: 'AWS CloudFront',
    vendor: 'Amazon Web Services',
    category: 'Cloud',
    description: 'CDN access logs from Amazon CloudFront distributions.',
    fieldGroups: [api()],
    docsUrl: 'https://docs.aws.amazon.com/AmazonCloudFront/latest/DeveloperGuide/AccessLogs.html',
  },
  {
    id: 'aws-waf',
    name: 'AWS WAF',
    vendor: 'Amazon Web Services',
    category: 'Cloud',
    description: 'Web ACL request logs from AWS WAF.',
    fieldGroups: [api()],
    docsUrl: 'https://docs.aws.amazon.com/waf/latest/developerguide',
  },
  {
    id: 'azure-activity-logs',
    name: 'Azure Activity Logs',
    vendor: 'Microsoft',
    category: 'Cloud',
    description: 'Subscription-level events from Azure Activity Log (control plane actions).',
    fieldGroups: [{ title: 'Azure AD', fields: [
      { key: 'tenant_id',     label: 'Tenant ID',     type: 'text',     placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx', required: true },
      { key: 'client_id',     label: 'Client ID',     type: 'text',     placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx', required: true },
      { key: 'client_secret', label: 'Client Secret', type: 'password', placeholder: 'App registration secret',             required: true },
    ]}],
    docsUrl: 'https://docs.microsoft.com/azure/azure-monitor/essentials/activity-log',
  },
  {
    id: 'azure-app-gateway',
    name: 'Azure Application Gateway',
    vendor: 'Microsoft',
    category: 'Cloud',
    description: 'WAF and access logs from Azure Application Gateway.',
    fieldGroups: [api()],
    docsUrl: 'https://docs.microsoft.com/azure/application-gateway/application-gateway-diagnostics',
  },
  {
    id: 'azure-nsg-flow',
    name: 'Azure NSG Flow Logs',
    vendor: 'Microsoft',
    category: 'Cloud',
    description: 'Network Security Group flow logs via Azure Network Watcher.',
    fieldGroups: [api()],
    docsUrl: 'https://docs.microsoft.com/azure/network-watcher/network-watcher-nsg-flow-logging-overview',
  },
  {
    id: 'azure-key-vault',
    name: 'Azure Key Vault',
    vendor: 'Microsoft',
    category: 'Cloud',
    description: 'Diagnostic logs from Azure Key Vault for secrets and certificate access.',
    fieldGroups: [api()],
    docsUrl: 'https://docs.microsoft.com/azure/key-vault/general/logging',
  },
  {
    id: 'azure-front-door',
    name: 'Azure Front Door',
    vendor: 'Microsoft',
    category: 'Cloud',
    description: 'WAF and access logs from Azure Front Door.',
    fieldGroups: [api()],
    docsUrl: 'https://docs.microsoft.com/azure/frontdoor/front-door-diagnostics',
  },
  {
    id: 'gcp-audit',
    name: 'Google Cloud Audit Logs',
    vendor: 'Google Cloud',
    category: 'Cloud',
    description: 'Admin Activity, Data Access and System Event audit logs from GCP.',
    fieldGroups: [{ title: 'Service Account', fields: [
      { key: 'project_id',        label: 'Project ID',            type: 'text',     placeholder: 'my-gcp-project',    required: true },
      { key: 'service_account_key', label: 'Service Account JSON', type: 'password', placeholder: 'Paste JSON key file', required: true },
      { key: 'pubsub_topic',      label: 'Pub/Sub Topic',          type: 'text',     placeholder: 'projects/.../topics/...', required: true },
    ]}],
    docsUrl: 'https://cloud.google.com/logging/docs/audit',
  },
  {
    id: 'gcp-vpc-flow',
    name: 'Google Cloud VPC Flow Logs',
    vendor: 'Google Cloud',
    category: 'Cloud',
    description: 'VPC network flow logs from Google Cloud.',
    fieldGroups: [api()],
    docsUrl: 'https://cloud.google.com/vpc/docs/flow-logs',
  },
  {
    id: 'gcp-load-balancing',
    name: 'Google Cloud Load Balancing',
    vendor: 'Google Cloud',
    category: 'Cloud',
    description: 'HTTP(S) load balancer access logs from Google Cloud.',
    fieldGroups: [api()],
    docsUrl: 'https://cloud.google.com/load-balancing/docs/https/https-logging-monitoring',
  },
  {
    id: 'gke',
    name: 'Google Kubernetes Engine',
    vendor: 'Google Cloud',
    category: 'Cloud',
    description: 'Container and audit logs from Google Kubernetes Engine.',
    fieldGroups: [api()],
    docsUrl: 'https://cloud.google.com/kubernetes-engine/docs/how-to/audit-logging',
  },
  {
    id: 'google-workspace',
    name: 'Google Workspace',
    vendor: 'Google',
    category: 'Cloud',
    description: 'Admin, login and Drive audit logs from Google Workspace.',
    fieldGroups: [oauth([{ key: 'customer_id', label: 'Customer ID', type: 'text', placeholder: 'Google Workspace Customer ID', required: true }])],
    docsUrl: 'https://developers.google.com/admin-sdk/reports/v1/get-start/getting-started',
  },
  {
    id: 'kubernetes-audit',
    name: 'Kubernetes Audit Logs',
    vendor: 'CNCF',
    category: 'Cloud',
    description: 'Kubernetes API server audit logs for cluster security monitoring.',
    fieldGroups: [api()],
    docsUrl: 'https://kubernetes.io/docs/tasks/debug/debug-cluster/audit/',
  },
  {
    id: 'wiz-audit',
    name: 'Wiz Audit Logs',
    vendor: 'Wiz',
    category: 'Cloud',
    description: 'User activity and configuration change audit logs from Wiz.',
    fieldGroups: [api()],
    docsUrl: 'https://docs.wiz.io',
  },
  {
    id: 'wiz-issues',
    name: 'Wiz Issues',
    vendor: 'Wiz',
    category: 'Cloud',
    description: 'Cloud security issues and misconfigurations detected by Wiz.',
    fieldGroups: [api()],
    docsUrl: 'https://docs.wiz.io',
  },
  {
    id: 'wiz-findings',
    name: 'Wiz Cloud Configuration Findings',
    vendor: 'Wiz',
    category: 'Cloud',
    description: 'CSPM configuration findings from Wiz.',
    fieldGroups: [api()],
    docsUrl: 'https://docs.wiz.io',
  },
  {
    id: 'wiz-threat',
    name: 'Wiz Threat Detections',
    vendor: 'Wiz',
    category: 'Cloud',
    description: 'Runtime threat detections from Wiz cloud workload protection.',
    fieldGroups: [api()],
    docsUrl: 'https://docs.wiz.io',
  },
  {
    id: 'wiz-vuln',
    name: 'Wiz Vulnerability Findings',
    vendor: 'Wiz',
    category: 'Cloud',
    description: 'Container and OS vulnerability scan results from Wiz.',
    fieldGroups: [api()],
    docsUrl: 'https://docs.wiz.io',
  },
  {
    id: 'lacework',
    name: 'Lacework / Fortinet FortiCNAPP',
    vendor: 'Fortinet',
    category: 'Cloud',
    description: 'Cloud-native application protection platform (CNAPP) from Lacework.',
    fieldGroups: [api()],
    docsUrl: 'https://docs.lacework.net',
  },
  {
    id: 'varonis',
    name: 'Varonis Data Security',
    vendor: 'Varonis',
    category: 'Cloud',
    description: 'Data security and insider threat detection from Varonis.',
    fieldGroups: [api()],
    docsUrl: 'https://docs.varonis.com',
  },
  {
    id: 'salesforce',
    name: 'Salesforce',
    vendor: 'Salesforce',
    category: 'Cloud',
    description: 'Event monitoring and login history logs from Salesforce.',
    fieldGroups: [oauth()],
    docsUrl: 'https://developer.salesforce.com/docs/atlas.en-us.api_rest.meta/api_rest',
  },
  {
    id: 'github-audit',
    name: 'GitHub Audit Logs',
    vendor: 'GitHub',
    category: 'Cloud',
    description: 'Organisation-level audit events from GitHub.',
    fieldGroups: [api([{ key: 'org', label: 'Organisation', type: 'text', placeholder: 'my-github-org', required: true }])],
    docsUrl: 'https://docs.github.com/en/enterprise-cloud@latest/admin/monitoring-activity-in-your-enterprise/reviewing-audit-logs-for-your-enterprise',
  },
  {
    id: 'new-relic',
    name: 'New Relic',
    vendor: 'New Relic',
    category: 'Cloud',
    description: 'Observability and log management events from New Relic.',
    fieldGroups: [api([{ key: 'account_id', label: 'Account ID', type: 'text', placeholder: 'New Relic Account ID', required: true }])],
    docsUrl: 'https://docs.newrelic.com',
  },
  {
    id: 'veeam',
    name: 'Veeam Backup & Replication',
    vendor: 'Veeam',
    category: 'Cloud',
    description: 'Backup job and session logs from Veeam Backup & Replication.',
    fieldGroups: [api()],
    docsUrl: 'https://www.veeam.com/documentation-guides-datasheets.html',
  },

  // ══════════════════════════════════════════
  // IAM / PAM
  // ══════════════════════════════════════════
  {
    id: 'okta',
    name: 'Okta',
    vendor: 'Okta',
    category: 'IAM',
    description: 'Identity and access management platform. Stream audit logs and authentication events from Okta.',
    fieldGroups: [{ title: 'Okta API', fields: [
      { key: 'domain',  label: 'Okta Domain', type: 'text',     placeholder: 'yourorg.okta.com',        required: true },
      { key: 'api_key', label: 'API Token',   type: 'password', placeholder: 'Okta API token (SSWS …)', required: true },
    ]}],
    docsUrl: 'https://developer.okta.com/docs/reference/api/system-log/',
  },
  {
    id: 'active-directory',
    name: 'Microsoft Active Directory',
    vendor: 'Microsoft',
    category: 'IAM',
    description: 'On-premises Active Directory security event logs via Azure Arc or MMA.',
    fieldGroups: [{ title: 'Domain Controller', fields: [
      { key: 'dc_host',  label: 'Domain Controller', type: 'text',     placeholder: 'dc01.corp.local', required: true },
      { key: 'username', label: 'Service Account',   type: 'text',     placeholder: 'svc-siem',        required: true },
      { key: 'password', label: 'Password',           type: 'password', placeholder: '••••••••',        required: true },
    ]}],
    docsUrl: 'https://docs.microsoft.com/azure/sentinel/connect-windows-security-events',
  },
  {
    id: 'azure-ad-entra',
    name: 'Microsoft Entra ID (Azure AD)',
    vendor: 'Microsoft',
    category: 'IAM',
    description: 'Sign-in, audit and identity protection logs from Microsoft Entra ID via Graph API.',
    fieldGroups: [oauth()],
    docsUrl: 'https://docs.microsoft.com/azure/active-directory/reports-monitoring',
  },
  {
    id: 'cyberark',
    name: 'CyberArk Vault',
    vendor: 'CyberArk',
    category: 'IAM',
    description: 'Privileged account security with PAM event logs from CyberArk.',
    fieldGroups: [{ title: 'Vault Connection', fields: [
      { key: 'vault_address', label: 'Vault Address', type: 'text',     placeholder: 'https://cyberark.company.com', required: true },
      { key: 'username',      label: 'Username',      type: 'text',     placeholder: 'CyberArk user',               required: true },
      { key: 'password',      label: 'Password',      type: 'password', placeholder: '••••••••',                    required: true },
    ]}],
    docsUrl: 'https://docs.cyberark.com',
  },
  {
    id: 'beyondtrust-pra',
    name: 'BeyondTrust Privileged Remote Access',
    vendor: 'BeyondTrust',
    category: 'IAM',
    description: 'Privileged remote access session, syslog and vault activity logs.',
    fieldGroups: [api()],
    docsUrl: 'https://www.beyondtrust.com/docs/privileged-remote-access/',
  },
  {
    id: 'cisco-duo',
    name: 'Cisco Duo Security',
    vendor: 'Cisco',
    category: 'IAM',
    description: 'Multi-factor authentication and device trust logs from Cisco Duo.',
    fieldGroups: [{ title: 'Duo Admin API', fields: [
      { key: 'api_host',       label: 'API Hostname',       type: 'text',     placeholder: 'api-xxxxxxxx.duosecurity.com', required: true },
      { key: 'integration_key', label: 'Integration Key',  type: 'text',     placeholder: 'DIXXX…',                      required: true },
      { key: 'secret_key',      label: 'Secret Key',       type: 'password', placeholder: 'Duo Secret Key',              required: true },
    ]}],
    docsUrl: 'https://duo.com/docs/adminapi',
  },
  {
    id: 'delinea-platform',
    name: 'Delinea Platform',
    vendor: 'Delinea',
    category: 'IAM',
    description: 'Privileged access management audit logs from Delinea (formerly Centrify / Thycotic).',
    fieldGroups: [api()],
    docsUrl: 'https://docs.delinea.com',
  },
  {
    id: 'one-identity-sps',
    name: 'One Identity Safeguard for Privileged Sessions',
    vendor: 'One Identity',
    category: 'IAM',
    description: 'Privileged session recordings and audit logs from One Identity SPS.',
    fieldGroups: [api()],
    docsUrl: 'https://support.oneidentity.com/safeguard-for-privileged-sessions/',
  },
  {
    id: 'wallix-bastion',
    name: 'Wallix Bastion',
    vendor: 'Wallix',
    category: 'IAM',
    description: 'Privileged access management session and audit logs from Wallix Bastion.',
    fieldGroups: [api()],
    docsUrl: 'https://www.wallix.com/resources/documentation/',
  },
  {
    id: 'jumpcloud',
    name: 'JumpCloud Directory Insights',
    vendor: 'JumpCloud',
    category: 'IAM',
    description: 'Directory and SSO audit events from JumpCloud.',
    fieldGroups: [api()],
    docsUrl: 'https://support.jumpcloud.com/s/article/Directory-Insights-Intro',
  },
  {
    id: 'keycloak',
    name: 'Keycloak',
    vendor: 'Red Hat',
    category: 'IAM',
    description: 'Open-source identity and access management audit events from Keycloak.',
    fieldGroups: [api()],
    docsUrl: 'https://www.keycloak.org/documentation',
  },
  {
    id: 'ping-identity',
    name: 'Ping Identity – PingFederate',
    vendor: 'Ping Identity',
    category: 'IAM',
    description: 'Enterprise federation and SSO audit logs from PingFederate.',
    fieldGroups: [api()],
    docsUrl: 'https://docs.pingidentity.com',
  },
  {
    id: 'rsa-securid',
    name: 'RSA SecurID',
    vendor: 'RSA',
    category: 'IAM',
    description: 'Authentication manager logs from RSA SecurID.',
    fieldGroups: [syslog()],
    docsUrl: 'https://community.rsa.com/docs',
  },
  {
    id: 'manageengine-ad-audit',
    name: 'ManageEngine ADAudit Plus',
    vendor: 'ManageEngine',
    category: 'IAM',
    description: 'Real-time Active Directory auditing from ManageEngine ADAudit Plus.',
    fieldGroups: [api()],
    docsUrl: 'https://www.manageengine.com/products/active-directory-audit/',
  },
  {
    id: 'cisco-ise',
    name: 'Cisco ISE',
    vendor: 'Cisco',
    category: 'IAM',
    description: 'Network access control and AAA logs from Cisco Identity Services Engine.',
    fieldGroups: [syslog()],
    docsUrl: 'https://www.cisco.com/c/en/us/products/security/identity-services-engine/',
  },
  {
    id: 'microsoft-intune',
    name: 'Microsoft Intune',
    vendor: 'Microsoft',
    category: 'IAM',
    description: 'Mobile device management and endpoint compliance logs from Microsoft Intune.',
    fieldGroups: [oauth()],
    docsUrl: 'https://docs.microsoft.com/mem/intune/',
  },
  {
    id: 'broadcom-siteminder',
    name: 'Broadcom SiteMinder',
    vendor: 'Broadcom',
    category: 'IAM',
    description: 'Web access management and SSO logs from Broadcom SiteMinder.',
    fieldGroups: [syslog()],
    docsUrl: 'https://techdocs.broadcom.com',
  },
  {
    id: 'ivanti-pulse',
    name: 'Ivanti / Pulse Connect Secure',
    vendor: 'Ivanti',
    category: 'IAM',
    description: 'SSL VPN and zero-trust access logs from Ivanti Secure Access.',
    fieldGroups: [syslog()],
    docsUrl: 'https://www.ivanti.com/products/ivanti-secure-access-client',
  },
  {
    id: 'rubycat-prove-it',
    name: 'Rubycat PROVE IT',
    vendor: 'Rubycat',
    category: 'IAM',
    description: 'Privileged access session recording and audit from Rubycat PROVE IT.',
    fieldGroups: [syslog()],
    docsUrl: 'https://www.rubycat.eu',
  },
  {
    id: 'openldap',
    name: 'OpenLDAP',
    vendor: 'OpenLDAP',
    category: 'IAM',
    description: 'Directory service authentication and access logs from OpenLDAP.',
    fieldGroups: [syslog()],
    docsUrl: 'https://www.openldap.org/doc/',
  },
  {
    id: 'openvpn',
    name: 'OpenVPN',
    vendor: 'OpenVPN',
    category: 'IAM',
    description: 'VPN connection and authentication logs from OpenVPN.',
    fieldGroups: [syslog()],
    docsUrl: 'https://openvpn.net/vpn-server-resources/',
  },
  {
    id: 'tenable-identity',
    name: 'Tenable Identity Exposure',
    vendor: 'Tenable',
    category: 'IAM',
    description: 'Active Directory identity threat detection from Tenable (formerly Alsid).',
    fieldGroups: [api()],
    docsUrl: 'https://docs.tenable.com/identity-exposure',
  },
  {
    id: 'lockself',
    name: 'LockSelf',
    vendor: 'LockSelf',
    category: 'IAM',
    description: 'Privileged password management and file transfer audit logs from LockSelf.',
    fieldGroups: [api()],
    docsUrl: 'https://www.lockself.com',
  },
  {
    id: 'systancia-cleanroom',
    name: 'Systancia Cleanroom',
    vendor: 'Systancia',
    category: 'IAM',
    description: 'Privileged access isolation and audit from Systancia Cleanroom.',
    fieldGroups: [syslog()],
    docsUrl: 'https://www.systancia.com/en/products/cleanroom/',
  },

  // ══════════════════════════════════════════
  // Email Security
  // ══════════════════════════════════════════
  {
    id: 'microsoft-365',
    name: 'Microsoft 365 / Office 365',
    vendor: 'Microsoft',
    category: 'Email',
    description: 'Email, SharePoint, Teams and admin audit events via Microsoft 365 Management APIs.',
    fieldGroups: [oauth()],
    docsUrl: 'https://docs.microsoft.com/office/office-365-management-api/',
  },
  {
    id: 'microsoft-365-message-trace',
    name: 'Microsoft 365 Message Trace',
    vendor: 'Microsoft',
    category: 'Email',
    description: 'Email delivery and message trace data from Microsoft 365 via Graph API.',
    fieldGroups: [oauth()],
    docsUrl: 'https://docs.microsoft.com/exchange/monitoring/trace-an-email-message/run-a-message-trace-and-view-results',
  },
  {
    id: 'mimecast',
    name: 'Mimecast Email Security',
    vendor: 'Mimecast',
    category: 'Email',
    description: 'Email security logs and threat events from Mimecast.',
    fieldGroups: [{ title: 'Mimecast API', fields: [
      { key: 'base_url',       label: 'Base URL',        type: 'text',     placeholder: 'https://us-api.mimecast.com', required: true },
      { key: 'access_key',     label: 'Access Key',      type: 'text',     placeholder: 'Mimecast Access Key',        required: true },
      { key: 'secret_key',     label: 'Secret Key',      type: 'password', placeholder: 'Mimecast Secret Key',        required: true },
      { key: 'application_id', label: 'Application ID',  type: 'text',     placeholder: 'App ID',                     required: true },
    ]}],
    docsUrl: 'https://developer.services.mimecast.com',
  },
  {
    id: 'proofpoint-tap',
    name: 'Proofpoint TAP',
    vendor: 'Proofpoint',
    category: 'Email',
    description: 'Advanced email threat events from Proofpoint Targeted Attack Protection.',
    fieldGroups: [api([{ key: 'principal', label: 'Service Principal', type: 'text', placeholder: 'TAP service principal', required: true }])],
    docsUrl: 'https://help.proofpoint.com/Threat_Insight_Dashboard/API_Documentation',
  },
  {
    id: 'proofpoint-pod',
    name: 'Proofpoint PoD',
    vendor: 'Proofpoint',
    category: 'Email',
    description: 'Email gateway logs from Proofpoint Protection on Demand.',
    fieldGroups: [api()],
    docsUrl: 'https://help.proofpoint.com',
  },
  {
    id: 'checkpoint-harmony-email',
    name: 'Check Point Harmony Email & Collaboration',
    vendor: 'Check Point',
    category: 'Email',
    description: 'Email and collaboration suite security events from Check Point Harmony.',
    fieldGroups: [api()],
    docsUrl: 'https://sc1.checkpoint.com/documents/Harmony_Email_and_Office',
  },
  {
    id: 'vade-cloud',
    name: 'Vade Cloud',
    vendor: 'Vade',
    category: 'Email',
    description: 'AI-powered email security events from Vade Cloud.',
    fieldGroups: [api()],
    docsUrl: 'https://www.vadesecure.com/en',
  },
  {
    id: 'vade-m365',
    name: 'Vade for M365',
    vendor: 'Vade',
    category: 'Email',
    description: 'Email phishing and malware detection events from Vade for Microsoft 365.',
    fieldGroups: [api()],
    docsUrl: 'https://www.vadesecure.com/en',
  },
  {
    id: 'trend-micro-email',
    name: 'Trend Micro Email Security',
    vendor: 'Trend Micro',
    category: 'Email',
    description: 'Email threat detection logs from Trend Micro Email Security.',
    fieldGroups: [api()],
    docsUrl: 'https://docs.trendmicro.com/en-us/enterprise/trend-micro-email-security',
  },
  {
    id: 'hornetsecurity',
    name: 'Hornetsecurity 365 Total Protection',
    vendor: 'Hornetsecurity',
    category: 'Email',
    description: 'Email security and archiving logs from Hornetsecurity.',
    fieldGroups: [api()],
    docsUrl: 'https://hornetsecurity.com/en',
  },
  {
    id: 'retarus',
    name: 'Retarus Email Security',
    vendor: 'Retarus',
    category: 'Email',
    description: 'Cloud email security gateway logs from Retarus.',
    fieldGroups: [api()],
    docsUrl: 'https://www.retarus.com/services/email-security/',
  },
  {
    id: 'apache-spamassassin',
    name: 'Apache SpamAssassin',
    vendor: 'Apache',
    category: 'Email',
    description: 'Open-source email spam filter logs from SpamAssassin.',
    fieldGroups: [syslog()],
    docsUrl: 'https://spamassassin.apache.org',
  },
  {
    id: 'postfix',
    name: 'Postfix',
    vendor: 'Postfix',
    category: 'Email',
    description: 'Mail transfer agent logs from Postfix SMTP server.',
    fieldGroups: [syslog()],
    docsUrl: 'http://www.postfix.org/documentation.html',
  },

  // ══════════════════════════════════════════
  // Threat Intelligence
  // ══════════════════════════════════════════
  {
    id: 'virustotal',
    name: 'VirusTotal',
    vendor: 'Google',
    category: 'Threat Intelligence',
    description: 'File, URL and IP reputation lookups from VirusTotal.',
    fieldGroups: [{ title: 'API Key', fields: [
      { key: 'api_key', label: 'API Key', type: 'password', placeholder: 'VirusTotal API key', required: true },
    ]}],
    docsUrl: 'https://developers.virustotal.com/reference',
  },
  {
    id: 'recorded-future',
    name: 'Recorded Future',
    vendor: 'Recorded Future',
    category: 'Threat Intelligence',
    description: 'Real-time threat intelligence from Recorded Future.',
    fieldGroups: [{ title: 'API Key', fields: [
      { key: 'api_key', label: 'API Token', type: 'password', placeholder: 'Recorded Future API token', required: true },
    ]}],
    docsUrl: 'https://api.recordedfuture.com',
  },
  {
    id: 'misp',
    name: 'MISP',
    vendor: 'CIRCL',
    category: 'Threat Intelligence',
    description: 'Open-source threat intelligence sharing platform.',
    fieldGroups: [{ title: 'MISP Server', fields: [
      { key: 'host',    label: 'MISP URL',  type: 'text',     placeholder: 'https://misp.company.com', required: true },
      { key: 'api_key', label: 'Auth Key',  type: 'password', placeholder: 'MISP auth key',            required: true },
    ]}],
    docsUrl: 'https://www.misp-project.org',
  },
  {
    id: 'anomali-threatstream',
    name: 'Anomali ThreatStream',
    vendor: 'Anomali',
    category: 'Threat Intelligence',
    description: 'Threat intelligence management and enrichment from Anomali ThreatStream.',
    fieldGroups: [api([{ key: 'username', label: 'Username', type: 'text', placeholder: 'Anomali username', required: true }])],
    docsUrl: 'https://www.anomali.com/platform/threatstream',
  },
  {
    id: 'opencti',
    name: 'OpenCTI',
    vendor: 'Filigran',
    category: 'Threat Intelligence',
    description: 'Open-source Cyber Threat Intelligence platform for IoC sharing and enrichment.',
    fieldGroups: [api()],
    docsUrl: 'https://docs.opencti.io',
  },
  {
    id: 'taxii',
    name: 'TAXII',
    vendor: 'OASIS',
    category: 'Threat Intelligence',
    description: 'Consume threat intelligence from any TAXII 2.x feed.',
    fieldGroups: [{ title: 'TAXII Server', fields: [
      { key: 'host',       label: 'Server URL',  type: 'text',     placeholder: 'https://taxii.example.com', required: true },
      { key: 'collection', label: 'Collection',  type: 'text',     placeholder: 'Collection ID',            required: true },
      { key: 'username',   label: 'Username',    type: 'text',     placeholder: 'Username (if required)',   required: false },
      { key: 'password',   label: 'Password',    type: 'password', placeholder: 'Password (if required)',   required: false },
    ]}],
    docsUrl: 'https://oasis-open.github.io/cti-documentation/taxii',
  },
  {
    id: 'stix',
    name: 'STIX',
    vendor: 'OASIS',
    category: 'Threat Intelligence',
    description: 'Ingest structured threat intelligence in STIX 2.x format.',
    fieldGroups: [{ title: 'Feed URL', fields: [
      { key: 'feed_url', label: 'STIX Feed URL', type: 'text', placeholder: 'https://threat-feed.example.com/stix', required: true },
    ]}],
    docsUrl: 'https://oasis-open.github.io/cti-documentation/stix',
  },
  {
    id: 'threatquotient',
    name: 'ThreatQuotient',
    vendor: 'ThreatQuotient',
    category: 'Threat Intelligence',
    description: 'Threat intelligence management and operations platform.',
    fieldGroups: [api()],
    docsUrl: 'https://threatq.com',
  },
  {
    id: 'censys',
    name: 'Censys',
    vendor: 'Censys',
    category: 'Threat Intelligence',
    description: 'Internet-wide scanning and attack surface management from Censys.',
    fieldGroups: [{ title: 'Censys API', fields: [
      { key: 'api_id',     label: 'API ID',     type: 'text',     placeholder: 'Censys API ID',     required: true },
      { key: 'api_secret', label: 'API Secret', type: 'password', placeholder: 'Censys API Secret', required: true },
    ]}],
    docsUrl: 'https://search.censys.io/api',
  },
  {
    id: 'shodan',
    name: 'Shodan',
    vendor: 'Shodan',
    category: 'Threat Intelligence',
    description: 'Internet-facing asset discovery and vulnerability intelligence from Shodan.',
    fieldGroups: [{ title: 'API Key', fields: [
      { key: 'api_key', label: 'API Key', type: 'password', placeholder: 'Shodan API key', required: true },
    ]}],
    docsUrl: 'https://developer.shodan.io',
  },
  {
    id: 'hatching-triage',
    name: 'Hatching Triage',
    vendor: 'Hatching',
    category: 'Threat Intelligence',
    description: 'Malware analysis sandbox from Hatching Triage.',
    fieldGroups: [api()],
    docsUrl: 'https://tria.ge/docs/cloud-api/',
  },
  {
    id: 'onyphe',
    name: 'Onyphe',
    vendor: 'Onyphe',
    category: 'Threat Intelligence',
    description: 'Cyber defence search engine for exposed asset intelligence.',
    fieldGroups: [api()],
    docsUrl: 'https://www.onyphe.io/documentation/api',
  },
  {
    id: 'cognyte-nexyte',
    name: 'Cognyte Nexyte',
    vendor: 'Cognyte',
    category: 'Threat Intelligence',
    description: 'Dark web and external threat intelligence from Cognyte Nexyte.',
    fieldGroups: [api()],
    docsUrl: 'https://www.cognyte.com',
  },
  {
    id: 'reliaquest-greymatter',
    name: 'ReliaQuest GreyMatter / Digital Shadows',
    vendor: 'ReliaQuest',
    category: 'Threat Intelligence',
    description: 'Digital risk protection and threat intelligence from ReliaQuest GreyMatter.',
    fieldGroups: [api()],
    docsUrl: 'https://www.reliaquest.com',
  },
  {
    id: 'prodaft-usta',
    name: 'Prodaft USTA',
    vendor: 'Prodaft',
    category: 'Threat Intelligence',
    description: 'Threat intelligence and adversary tracking from Prodaft USTA platform.',
    fieldGroups: [api()],
    docsUrl: 'https://www.prodaft.com',
  },
  {
    id: 'glimps',
    name: 'Glimps',
    vendor: 'Glimps',
    category: 'Threat Intelligence',
    description: 'Malware detection and threat intelligence from Glimps AI analysis.',
    fieldGroups: [api()],
    docsUrl: 'https://www.glimps.fr',
  },
  {
    id: 'mwdb',
    name: 'MWDB',
    vendor: 'CERT.pl',
    category: 'Threat Intelligence',
    description: 'Malware database and configuration extraction from MWDB.',
    fieldGroups: [api()],
    docsUrl: 'https://mwdb.readthedocs.io',
  },
  {
    id: 'tranco',
    name: 'Tranco',
    vendor: 'Tranco',
    category: 'Threat Intelligence',
    description: 'Domain ranking list for filtering known-good domains during IOC enrichment.',
    fieldGroups: [{ title: 'Feed URL', fields: [
      { key: 'list_id', label: 'Tranco List ID', type: 'text', placeholder: 'latest or specific ID', required: false },
    ]}],
    docsUrl: 'https://tranco-list.eu',
  },
  {
    id: 'aleph-alerts',
    name: 'Aleph Alerts',
    vendor: 'Aleph Networks',
    category: 'Threat Intelligence',
    description: 'Darknet and cybercriminal intelligence alerts from Aleph.',
    fieldGroups: [api()],
    docsUrl: 'https://aleph-networks.eu',
  },
  {
    id: 'certificate-transparency',
    name: 'Certificate Transparency',
    vendor: 'Various',
    category: 'Threat Intelligence',
    description: 'Monitor Certificate Transparency logs for suspicious domain certificates.',
    fieldGroups: [{ title: 'CT Log Feed', fields: [
      { key: 'domains', label: 'Monitored Domains', type: 'text', placeholder: 'example.com, example.org', required: true },
    ]}],
    docsUrl: 'https://certificate.transparency.dev',
  },

  // ══════════════════════════════════════════
  // SOAR / Ticketing / Collaboration
  // ══════════════════════════════════════════
  {
    id: 'servicenow',
    name: 'ServiceNow',
    vendor: 'ServiceNow',
    category: 'SOAR',
    description: 'IT service management and security operations ticketing via ServiceNow.',
    fieldGroups: [{ title: 'Instance', fields: [
      { key: 'instance', label: 'Instance URL', type: 'text',     placeholder: 'https://yourcompany.service-now.com', required: true },
      { key: 'username', label: 'Username',     type: 'text',     placeholder: 'integration-user',                   required: true },
      { key: 'password', label: 'Password',     type: 'password', placeholder: '••••••••',                           required: true },
    ]}],
    docsUrl: 'https://developer.servicenow.com',
  },
  {
    id: 'pagerduty',
    name: 'PagerDuty',
    vendor: 'PagerDuty',
    category: 'SOAR',
    description: 'Incident alert routing and on-call management from PagerDuty.',
    fieldGroups: [api()],
    docsUrl: 'https://developer.pagerduty.com/api-reference/',
  },
  {
    id: 'jira',
    name: 'Atlassian Jira',
    vendor: 'Atlassian',
    category: 'SOAR',
    description: 'Issue and project tracking integration with Atlassian Jira.',
    fieldGroups: [{ title: 'Jira Connection', fields: [
      { key: 'host',     label: 'Jira Host',      type: 'text',     placeholder: 'https://yourcompany.atlassian.net', required: true },
      { key: 'email',    label: 'Email',           type: 'text',     placeholder: 'admin@company.com',                required: true },
      { key: 'api_key',  label: 'API Token',       type: 'password', placeholder: 'Jira API token',                   required: true },
      { key: 'project',  label: 'Project Key',     type: 'text',     placeholder: 'SEC',                              required: false },
    ]}],
    docsUrl: 'https://developer.atlassian.com/cloud/jira/platform/rest/v3/',
  },
  {
    id: 'thehive',
    name: 'The Hive',
    vendor: 'TheHive Project',
    category: 'SOAR',
    description: 'Open-source scalable security incident response platform.',
    fieldGroups: [api()],
    docsUrl: 'https://docs.thehive-project.org',
  },
  {
    id: 'fortinet-fortisoar',
    name: 'Fortinet FortiSOAR',
    vendor: 'Fortinet',
    category: 'SOAR',
    description: 'Security orchestration, automation and response platform from Fortinet.',
    fieldGroups: [api()],
    docsUrl: 'https://docs.fortinet.com/fortisoar',
  },
  {
    id: 'palo-alto-xsoar',
    name: 'Palo Alto Cortex XSOAR',
    vendor: 'Palo Alto Networks',
    category: 'SOAR',
    description: 'Security orchestration, automation and response from Palo Alto Cortex XSOAR.',
    fieldGroups: [api()],
    docsUrl: 'https://docs.paloaltonetworks.com/cortex/cortex-xsoar',
  },
  {
    id: 'splunk-soar',
    name: 'Splunk SOAR',
    vendor: 'Splunk',
    category: 'SOAR',
    description: 'Security orchestration and automated response from Splunk SOAR (Phantom).',
    fieldGroups: [api()],
    docsUrl: 'https://docs.splunk.com/Documentation/SOAR',
  },
  {
    id: 'swimlane-turbine-xdr',
    name: 'Swimlane Turbine',
    vendor: 'Swimlane',
    category: 'SOAR',
    description: 'Low-code security automation platform from Swimlane Turbine.',
    fieldGroups: [api()],
    docsUrl: 'https://swimlane.com',
  },

  // ══════════════════════════════════════════
  // OT / IoT
  // ══════════════════════════════════════════
  {
    id: 'nozomi-vantage',
    name: 'Nozomi Vantage',
    vendor: 'Nozomi Networks',
    category: 'OT/IoT',
    description: 'OT, IoT and ICS network visibility and threat detection from Nozomi.',
    fieldGroups: [api()],
    docsUrl: 'https://www.nozominetworks.com/resources/documentation/',
  },
  {
    id: 'nozomi-cmc',
    name: 'Nozomi Central Management Console',
    vendor: 'Nozomi Networks',
    category: 'OT/IoT',
    description: 'Centralised management and alert aggregation for Nozomi deployments.',
    fieldGroups: [api()],
    docsUrl: 'https://www.nozominetworks.com',
  },
  {
    id: 'claroty-xdome',
    name: 'Claroty xDome',
    vendor: 'Claroty',
    category: 'OT/IoT',
    description: 'Industrial cybersecurity and OT/IoT asset management from Claroty xDome.',
    fieldGroups: [api()],
    docsUrl: 'https://www.claroty.com',
  },
  {
    id: 'seckiot-citadelle',
    name: 'Seckiot Citadelle',
    vendor: 'Seckiot',
    category: 'OT/IoT',
    description: 'ICS/SCADA network monitoring and anomaly detection from Seckiot.',
    fieldGroups: [syslog()],
    docsUrl: 'https://www.seckiot.fr',
  },
  {
    id: 'thinkst-canary',
    name: 'Thinkst Canary',
    vendor: 'Thinkst',
    category: 'OT/IoT',
    description: 'Deception technology honeypot alerts from Thinkst Canary.',
    fieldGroups: [api()],
    docsUrl: 'https://help.canary.tools',
  },
  {
    id: 'trapster',
    name: 'Trapster (by Ballpoint)',
    vendor: 'Ballpoint',
    category: 'OT/IoT',
    description: 'Honeytoken and deception alerts from Trapster.',
    fieldGroups: [api()],
    docsUrl: 'https://www.ballpointsecurity.com',
  },
  {
    id: 'neroswarm-honeypot',
    name: 'NeroSwarm Honeypot',
    vendor: 'NeroSwarm',
    category: 'OT/IoT',
    description: 'Honeypot alert events from NeroSwarm.',
    fieldGroups: [api()],
    docsUrl: 'https://neroswarm.com',
  },

  // ══════════════════════════════════════════
  // Vulnerability Management
  // ══════════════════════════════════════════
  {
    id: 'tenable-vm',
    name: 'Tenable Vulnerability Management',
    vendor: 'Tenable',
    category: 'Vulnerability',
    description: 'Continuous vulnerability assessment and asset inventory from Tenable.io.',
    fieldGroups: [api()],
    docsUrl: 'https://docs.tenable.com/vulnerability-management',
  },
  {
    id: 'cyberwatch',
    name: 'Cyberwatch Vulnerability Manager',
    vendor: 'Cyberwatch',
    category: 'Vulnerability',
    description: 'Vulnerability management and patch prioritisation from Cyberwatch.',
    fieldGroups: [api()],
    docsUrl: 'https://docs.cyberwatch.fr',
  },
  {
    id: 'bitsight-spm',
    name: 'Bitsight Security Performance Management',
    vendor: 'Bitsight',
    category: 'Vulnerability',
    description: 'Continuous external attack surface monitoring and ratings from Bitsight.',
    fieldGroups: [api()],
    docsUrl: 'https://help.bitsighttech.com',
  },
  {
    id: 'securityscorecard',
    name: 'SecurityScorecard VAS',
    vendor: 'SecurityScorecard',
    category: 'Vulnerability',
    description: 'Vendor attack surface and cyber risk ratings from SecurityScorecard.',
    fieldGroups: [api()],
    docsUrl: 'https://securityscorecard.com/documentation/',
  },

  // ══════════════════════════════════════════
  // Others / Misc
  // ══════════════════════════════════════════
  {
    id: 'cef',
    name: 'Common Event Format (CEF)',
    vendor: 'ArcSight',
    category: 'Others',
    description: 'Generic CEF syslog receiver for any device that supports the Common Event Format.',
    fieldGroups: [syslog()],
    docsUrl: 'https://www.microfocus.com/documentation/arcsight/arcsight-smartconnectors-8.4/cef-implementation-standard/Content/CEF/Chapter%201%20What%20is%20CEF.htm',
  },
  {
    id: 'ocsf',
    name: 'OCSF',
    vendor: 'OCSF',
    category: 'Others',
    description: 'Open Cybersecurity Schema Framework normalised event stream.',
    fieldGroups: [api()],
    docsUrl: 'https://schema.ocsf.io',
  },
  {
    id: 'vmware-esxi',
    name: 'VMware ESXi',
    vendor: 'VMware',
    category: 'Others',
    description: 'Hypervisor system and authentication logs from VMware ESXi.',
    fieldGroups: [syslog()],
    docsUrl: 'https://docs.vmware.com/en/VMware-vSphere',
  },
  {
    id: 'vmware-vcenter',
    name: 'VMware vCenter',
    vendor: 'VMware',
    category: 'Others',
    description: 'vCenter Server audit and event logs for virtualisation management monitoring.',
    fieldGroups: [api()],
    docsUrl: 'https://docs.vmware.com/en/VMware-vSphere',
  },
  {
    id: 'ibm-aix',
    name: 'IBM AIX',
    vendor: 'IBM',
    category: 'Others',
    description: 'System and audit logs from IBM AIX Unix servers.',
    fieldGroups: [syslog()],
    docsUrl: 'https://www.ibm.com/docs/en/aix',
  },
  {
    id: 'ibm-iseries',
    name: 'IBM iSeries / AS400',
    vendor: 'IBM',
    category: 'Others',
    description: 'Security audit journal logs from IBM iSeries (AS/400) systems.',
    fieldGroups: [syslog()],
    docsUrl: 'https://www.ibm.com/docs/en/i',
  },
  {
    id: 'openssh',
    name: 'OpenSSH',
    vendor: 'OpenSSH',
    category: 'Others',
    description: 'SSH authentication and session logs from OpenSSH.',
    fieldGroups: [syslog()],
    docsUrl: 'https://www.openssh.com',
  },
  {
    id: 'openai',
    name: 'OpenAI',
    vendor: 'OpenAI',
    category: 'Others',
    description: 'AI enrichment and analysis actions via OpenAI API.',
    fieldGroups: [api([{ key: 'model', label: 'Model', type: 'text', placeholder: 'gpt-4o', required: false }])],
    docsUrl: 'https://platform.openai.com/docs',
  },
  {
    id: 'palo-alto-prisma',
    name: 'Palo Alto Prisma Access',
    vendor: 'Palo Alto Networks',
    category: 'Network',
    description: 'SASE network access and security logs from Palo Alto Prisma Access.',
    fieldGroups: [api()],
    docsUrl: 'https://docs.paloaltonetworks.com/prisma-access',
  },
  {
    id: 'slack',
    name: 'Slack',
    vendor: 'Slack',
    category: 'Others',
    description: 'Send automated alert notifications and SOC updates to Slack channels.',
    fieldGroups: [{ title: 'Webhook', fields: [
      { key: 'webhook_url', label: 'Webhook URL', type: 'text',     placeholder: 'https://hooks.slack.com/services/…', required: true },
      { key: 'bot_token',   label: 'Bot Token',   type: 'password', placeholder: 'xoxb-… (optional for advanced ops)',  required: false },
    ]}],
    docsUrl: 'https://api.slack.com',
  },
  {
    id: 'mattermost',
    name: 'Mattermost',
    vendor: 'Mattermost',
    category: 'Others',
    description: 'Open-source Slack alternative for alert routing and collaboration.',
    fieldGroups: [api([{ key: 'channel', label: 'Channel', type: 'text', placeholder: '#security-alerts', required: false }])],
    docsUrl: 'https://developers.mattermost.com',
  },
  {
    id: 'microsoft-teams',
    name: 'Microsoft Teams',
    vendor: 'Microsoft',
    category: 'Others',
    description: 'Send SOC notifications and alerts to Microsoft Teams channels.',
    fieldGroups: [{ title: 'Webhook', fields: [
      { key: 'webhook_url', label: 'Incoming Webhook URL', type: 'text', placeholder: 'https://xxx.webhook.office.com/…', required: true },
    ]}],
    docsUrl: 'https://docs.microsoft.com/microsoftteams/platform/webhooks-and-connectors/how-to/connectors-using',
  },
  {
    id: 'whois',
    name: 'Whois',
    vendor: 'Various',
    category: 'Threat Intelligence',
    description: 'Domain registration and ownership lookups via WHOIS.',
    fieldGroups: [{ title: 'Feed', fields: [
      { key: 'server', label: 'WHOIS Server (optional)', type: 'text', placeholder: 'whois.iana.org', required: false },
    ]}],
    docsUrl: 'https://www.iana.org/whois',
  },
  {
    id: 'raw-events',
    name: 'Raw Events',
    vendor: 'Generic',
    category: 'Others',
    description: 'Generic raw log ingestion for any source not covered by a specific connector.',
    fieldGroups: [syslog()],
    docsUrl: '',
  },
  {
    id: 'google-cloud-db-mysql',
    name: 'Azure Database for MySQL',
    vendor: 'Microsoft',
    category: 'Cloud',
    description: 'Audit and slow query logs from Azure Database for MySQL.',
    fieldGroups: [api()],
    docsUrl: 'https://docs.microsoft.com/azure/mysql',
  },
  {
    id: 'pradeo-mobile',
    name: 'Pradeo Mobile Threat Defense',
    vendor: 'Pradeo',
    category: 'Endpoint',
    description: 'Mobile application analysis and MTD alerts from Pradeo.',
    fieldGroups: [api()],
    docsUrl: 'https://www.pradeo.com',
  },
  {
    id: 'checkpoint-harmony-mobile',
    name: 'Check Point Harmony Mobile',
    vendor: 'Check Point',
    category: 'Endpoint',
    description: 'Mobile threat defence for iOS and Android from Check Point Harmony.',
    fieldGroups: [api()],
    docsUrl: 'https://sc1.checkpoint.com/documents/Harmony_Mobile',
  },
];

export const CATEGORIES = [
  'All',
  'SIEM',
  'Endpoint',
  'Network',
  'Cloud',
  'IAM',
  'Email',
  'Threat Intelligence',
  'SOAR',
  'OT/IoT',
  'Vulnerability',
  'Others',
];
